import React from "react";
import PillToggle from "./ui/PillToggle";
import ToggleSwitch from "./ui/ToggleSwitch";
import InputWithSuffix from "./ui/InputWithSuffix";
import InterestRateChip from "./ui/InterestRateChip";
import ClassificationSelector from "./ui/ClassificationSelector";

const RATE_TYPE_OPTIONS = [
  { value: "FLOATING", label: "משתנה" },
  { value: "FIXED", label: "קבוע" },
];

const DAY_COUNT_OPTIONS = [
  { value: "365", label: "365 ימים" },
  { value: "360", label: "360 ימים" },
];

/**
 * Tab 1 — פרטים עיקריים (Core Details)
 * Contains remainingPrincipal + full interest rate configuration card.
 */
export default function LoanFormTab1({
  loan,
  displayValues,
  errors,
  touched,
  baseRates,
  onChange,
  onBlur,
  onBlurNumber,
  negativeRateWarning,
  onMaturityShortcut,
}) {
  return (
    <div className="form-grid">

      {/* ── Classification ───────────────────────────────── */}
      <div style={{ gridColumn: "1 / -1" }}>
        <ClassificationSelector
          value={loan.classification}
          onChange={onChange}
          error={touched.classification && errors.classification}
        />
        {loan.classification === "OTHER" && (
          <div className="field" style={{ marginTop: "var(--space-sm)" }}>
            <label className="label-required">סיבה לסיווג "אחר"</label>
            <input
              className="input"
              name="classificationOtherReason"
              value={loan.classificationOtherReason}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="תאר את סוג ההלוואה"
            />
            {touched.classificationOtherReason && errors.classificationOtherReason && (
              <div className="field-error">{errors.classificationOtherReason}</div>
            )}
          </div>
        )}
      </div>

      {/* ── Bank Account ─────────────────────────────────── */}
      <div className="field">
        <label className="label-optional">מספר חשבון בנק <span className="optional-badge">(אופציונלי)</span></label>
        <input
          className="input"
          name="bankAccountNumber"
          value={loan.bankAccountNumber}
          onChange={onChange}
          onBlur={onBlur}
          placeholder="XX-XXXXX-XXXXXX"
        />
      </div>

      {/* ── Currency ─────────────────────────────────────── */}
      <div className="field">
        <label className="label-required">מטבע</label>
        <select
          className="select"
          name="currency"
          value={loan.currency}
          onChange={onChange}
          onBlur={onBlur}
        >
          <option value="ILS">₪ שקל (ILS)</option>
          <option value="USD">$ דולר (USD)</option>
          <option value="EUR">€ יורו (EUR)</option>
          <option value="GBP">£ פאונד (GBP)</option>
        </select>
        {touched.currency && errors.currency && (
          <div className="field-error">{errors.currency}</div>
        )}
      </div>

      {/* ── Principal ────────────────────────────────────── */}
      <div className="field">
        <label className="label-required">קרן</label>
        <input
          className="input"
          name="principal"
          type="text"
          value={displayValues.principal}
          onChange={onChange}
          onBlur={onBlurNumber}
          placeholder="0"
        />
        {touched.principal && errors.principal && (
          <div className="field-error">{errors.principal}</div>
        )}
      </div>

      {/* ── Origination Date ─────────────────────────────── */}
      <div className="field">
        <label className="label-optional">תאריך פתיחה <span className="optional-badge">(אופציונלי)</span></label>
        <input
          className="input"
          name="originationDate"
          type="date"
          value={loan.originationDate}
          onChange={onChange}
          onBlur={onBlur}
        />
      </div>

      {/* ── Maturity Date + Shortcuts ─────────────────────── */}
      <div className="field">
        <label className="label-optional">תאריך פירעון <span className="optional-badge">(אופציונלי)</span></label>
        <input
          className="input"
          name="maturityDate"
          type="date"
          value={loan.maturityDate}
          onChange={onChange}
          onBlur={onBlur}
        />
        <div className="lf-maturity-shortcuts">
          {[{ label: "6M", months: 6 }, { label: "1Y", months: 12 }, { label: "2Y", months: 24 }, { label: "5Y", months: 60 }].map(({ label, months }) => (
            <button
              key={label}
              type="button"
              className="lf-shortcut-pill"
              onClick={() => onMaturityShortcut(months)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Remaining Principal ──────────────────────────── */}
      <div className="field">
        <label className="label-optional">יתרת קרן <span className="optional-badge">(אופציונלי)</span></label>
        <input
          className="input"
          name="remainingPrincipal"
          type="text"
          value={displayValues.remainingPrincipal}
          onChange={onChange}
          onBlur={onBlurNumber}
          placeholder="0"
        />
      </div>

      {/* ── Interest Rate ────────────────────────────────── */}
      <div className="rate-section-card">
        <p className="rate-section-card-title">ריבית</p>
        {loan.rateType === "FLOATING" && (
          <p className="rate-section-card-subtitle">בסיס ריבית + מרווח = ריבית כוללת</p>
        )}

        <div className="field">
          <label className="label-required">סוג ריבית</label>
          <PillToggle
            name="rateType"
            value={loan.rateType}
            options={RATE_TYPE_OPTIONS}
            onChange={onChange}
          />
          {touched.rateType && errors.rateType && (
            <div className="field-error">{errors.rateType}</div>
          )}
        </div>

        <div className="field">
          <label className="label-optional" style={{ marginBottom: "var(--space-xs)" }}>
            לוח שנה
          </label>
          <PillToggle
            name="dayCountConvention"
            value={loan.dayCountConvention}
            options={DAY_COUNT_OPTIONS}
            onChange={onChange}
          />
        </div>

        {loan.rateType === "FLOATING" && (
          <>
            <div className="field form-full-width" style={{ gridColumn: "1 / -1" }}>
              <ToggleSwitch
                name="hasBaseRateCondition"
                checked={loan.hasBaseRateCondition}
                onChange={onChange}
                label="תנאי ריבית בסיס: הנמוכה מבין שתי ריביות"
              />
              {!loan.hasBaseRateCondition && (
                <p style={{ fontSize: "12px", color: "var(--text-secondary)", margin: "4px 0 0 46px" }}>
                  הפעל אם ריבית הבסיס תהיה המינימום בין שני מדדים (למשל: פריים לעומת מק"מ).
                </p>
              )}
            </div>

            {loan.hasBaseRateCondition ? (
              <>
                <div className="field">
                  <label className="label-required">ריבית בסיס ראשונה</label>
                  <select
                    className="select"
                    name="baseRateConditionId1"
                    value={loan.baseRateConditionId1}
                    onChange={onChange}
                    onBlur={onBlur}
                  >
                    <option value="">בחר ריבית בסיס</option>
                    {baseRates.map((br) => (
                      <option key={br._id} value={br._id}>{br.name}</option>
                    ))}
                  </select>
                  {touched.baseRateConditionId1 && errors.baseRateConditionId1 && (
                    <div className="field-error">{errors.baseRateConditionId1}</div>
                  )}
                </div>

                <div className="field">
                  <label className="label-required">ריבית בסיס שנייה</label>
                  <select
                    className="select"
                    name="baseRateConditionId2"
                    value={loan.baseRateConditionId2}
                    onChange={onChange}
                    onBlur={onBlur}
                  >
                    <option value="">בחר ריבית בסיס</option>
                    {baseRates.map((br) => (
                      <option key={br._id} value={br._id}>{br.name}</option>
                    ))}
                  </select>
                  {touched.baseRateConditionId2 && errors.baseRateConditionId2 && (
                    <div className="field-error">{errors.baseRateConditionId2}</div>
                  )}
                </div>
              </>
            ) : (
              <div className="field">
                <label className="label-required">בסיס ריבית</label>
                <select
                  className="select"
                  name="baseRateId"
                  value={loan.baseRateId}
                  onChange={onChange}
                  onBlur={onBlur}
                >
                  <option value="">בחר בסיס ריבית</option>
                  {baseRates.map((br) => (
                    <option key={br._id} value={br._id}>{br.name}</option>
                  ))}
                </select>
                {touched.baseRateId && errors.baseRateId && (
                  <div className="field-error">{errors.baseRateId}</div>
                )}
              </div>
            )}

            <div className="field">
              <label className="label-optional">מרווח</label>
              <InputWithSuffix
                name="margin"
                value={displayValues.margin}
                onChange={onChange}
                onBlur={onBlurNumber}
                placeholder="0"
              />
              {touched.margin && errors.margin && (
                <div className="field-error">{errors.margin}</div>
              )}
            </div>
          </>
        )}

        <div className="field">
          <label className="label-optional" style={{ marginBottom: "var(--space-xs)" }}>
            מע"מ על הריבית
          </label>
          <ToggleSwitch
            name="hasVAT"
            checked={loan.hasVAT}
            onChange={onChange}
            label='כולל מע"מ 18%'
          />
        </div>

        <div className="field form-full-width" style={{ gridColumn: "1 / -1" }}>
          {loan.rateType === "FIXED" ? (
            <>
              <label className="label-required">ריבית קבועה</label>
              <InputWithSuffix
                name="interestRate"
                value={displayValues.interestRate}
                onChange={onChange}
                onBlur={onBlurNumber}
                placeholder="0"
              />
            </>
          ) : (
            <>
              <label className="label-required">ריבית כוללת מחושבת</label>
              <InterestRateChip value={loan.interestRate} />
            </>
          )}
          {touched.interestRate && errors.interestRate && (
            <div className="field-error">{errors.interestRate}</div>
          )}
          {negativeRateWarning && (
            <div className="negative-rate-warning">
              ⚠ ריבית שלילית — ודא שזו הכוונה לפני שמירה.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
